import { createAsyncThunk } from "@reduxjs/toolkit";
import { showSnackbar } from "../slices/SnackbarSlice";
import {
  addReviewWidgetApi,
  editReviewWidgetApi,
  getReviewWidgetConfigApi,
  getReviewWidgetListApi,
  getReviewWidgetDetailApi,
  getReviewWidgetJsonldApi,
  regenerateReviewSummaryApi,
  IGetReviewWidgetConfigPayload,
  IGetReviewWidgetListPayload,
} from "../apis/ReviewWidgetApi";

/**
 * Create a new review widget.
 * Payload: { checkin_id, widget_name, ...optionalReviewKindFields }
 */
export const addReviewWidgetAction = createAsyncThunk(
  "reviewWidget/add",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await addReviewWidgetApi(payload);
      dispatch(
        showSnackbar({
          message: response?.data?.message || "Review widget created successfully!",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message || "Failed to create review widget",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to create review widget",
      );
    }
  },
);

/**
 * Edit review-kind-specific fields on an existing widget.
 * Payload: { widget_id, ...partialReviewKindFields }
 * For common fields (name, border_config, theme_config, cta_config, branding_config)
 * use editWidgetAction (shared endpoint) from WidgetActions.ts.
 */
export const editReviewWidgetAction = createAsyncThunk(
  "reviewWidget/edit",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await editReviewWidgetApi(payload);
      dispatch(
        showSnackbar({
          message: response?.data?.message || "Review widget updated successfully!",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message || "Failed to update review widget",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to update review widget",
      );
    }
  },
);

/**
 * Fetch the bootstrap config for the dashboard editor / preview.
 * Returns: widget doc, business branding, google_review_url, ai_summary.
 * Use this (not raw widget doc) to render the editor preview — guarantees
 * text_styles has all 9 keys populated.
 */
export const getReviewWidgetConfigAction = createAsyncThunk(
  "reviewWidget/getConfig",
  async (
    payload: IGetReviewWidgetConfigPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await getReviewWidgetConfigApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to fetch review widget config",
      );
    }
  },
);

/**
 * Fetch the paginated list of curated reviews for a widget.
 * All curation filters (rating threshold, text-only, keywords, hand-picked, sort) applied server-side.
 * Payload: { business_id, widget_id?, cursor?, limit?, ... }
 */
export const getReviewWidgetListAction = createAsyncThunk(
  "reviewWidget/getList",
  async (
    payload: IGetReviewWidgetListPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await getReviewWidgetListApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to fetch review widget list",
      );
    }
  },
);

/**
 * Fetch detail for a single review.
 */
export const getReviewWidgetDetailAction = createAsyncThunk(
  "reviewWidget/getDetail",
  async (
    payload: { business_id: string; widget_id?: string; review_id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await getReviewWidgetDetailApi(payload as any);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to fetch review detail",
      );
    }
  },
);

/**
 * Fetch JSON-LD structured data for SEO.
 */
export const getReviewWidgetJsonldAction = createAsyncThunk(
  "reviewWidget/getJsonld",
  async (
    payload: { business_id: string; widget_id?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await getReviewWidgetJsonldApi(payload as any);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to fetch review widget JSON-LD",
      );
    }
  },
);

/**
 * Trigger AI Review Summary regeneration for a business.
 */
export const regenerateReviewSummaryAction = createAsyncThunk(
  "reviewWidget/regenerateSummary",
  async (
    payload: { business_id: string; widget_id?: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await regenerateReviewSummaryApi(payload);
      dispatch(
        showSnackbar({
          message:
            response?.data?.message ||
            "AI Summary regenerated successfully!",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to regenerate AI summary",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to regenerate AI summary",
      );
    }
  },
);
